import cache from "./cache";
import audit from "../api/audit";
import { SCHEDULE_TYPES } from "../../../utils/constants";

type ServiceFormData = {
  streetFromId?: number;
  streetFromNumber?: number;
  streetFromLetter?: string;
  streetToId?: number;
  streetToNumber?: number;
  streetToLetter?: string;
  customerId?: number;
  subscriberId?: number;
  authorizerId?: number;
  authorizedId?: number;
  reqs?: number[];
  reqsDetails?: {
    added: number[];
    removed: number[];
  };
  reqsTaxiId?: number[];
  reqsDriverId?: number[];
  taxiFixedId?: number;
};

type ServiceFormDataResolved = ServiceFormData & {
  streetFrom?: string;
  streetTo?: string;
  customerName?: string;
  customerPhone?: string;
  subscriberName?: string;
  subscriberRef?: string;
  authorizerName?: string;
  authorizedName?: string;
  reqsNames?: string[];
  taxiFixedLicense?: string;
};

const resolveServiceData = async (
  wgId: number,
  centralId: number,
  data: ServiceFormData,
  originalData: object
) => {
  const resolved: ServiceFormDataResolved = {};
  if (data.streetFromId) {
    const street = await cache.getStreet(wgId, centralId, data.streetFromId);
    resolved.streetFrom = `${street?.display}, ${data.streetFromNumber}`;
    if (data.streetFromLetter) {
      resolved.streetFrom += ` ${data.streetFromLetter}`;
    }
  }
  if (data.streetToId) {
    const street = await cache.getStreet(wgId, centralId, data.streetToId);
    resolved.streetTo = `${street.display}, ${data.streetToNumber}`;
    if (data.streetToLetter) {
      resolved.streetTo += ` ${data.streetToLetter}`;
    }
  }
  if (data.customerId) {
    const customer = await cache.getCustomer(wgId, data.customerId);
    resolved.customerName = customer.name;
    resolved.customerPhone = customer.phone;
  }
  if (data.subscriberId) {
    const subscriber = await cache.getSubscriber(wgId, data.subscriberId);
    resolved.subscriberName = subscriber.name;
    resolved.subscriberRef = subscriber.ref;
  }
  if (data.authorizerId) {
    const authorizer = await cache.getSubscriberUser(wgId, data.authorizerId);
    resolved.authorizerName = authorizer.name;
  }
  if (data.authorizedId) {
    const authorized = await cache.getSubscriberUser(wgId, data.authorizedId);
    resolved.authorizedName = authorized.name;
  }

  const reqs = data.reqs || [];
  if (data.reqsTaxiId && data.reqsTaxiId.length > 0) {
    reqs.push(...data.reqsTaxiId);
    delete data.reqsTaxiId;
  }
  if (data.reqsDriverId && data.reqsDriverId.length > 0) {
    reqs.push(...data.reqsDriverId);
    delete data.reqsDriverId;
  }

  const originalReqs = (originalData as any).requirementsData?.requirements || [];

  if (!data.reqsDetails) {
    data.reqsDetails = {
      added: [],
      removed: [],
    };
  }
  data.reqsDetails.added = reqs.filter((req: number) => !originalReqs.includes(req)) || [];
  data.reqsDetails.removed = originalReqs.filter((req: number) => !reqs.includes(req)) || [];

  data.reqs = reqs;

  if (data.reqsDetails.added.length > 0) {
    resolved.reqsNames = [];
    for (const reqId of data.reqsDetails.added) {
      const req = await cache.getReq(wgId, reqId);
      resolved.reqsNames.push("+ " + req?.nameClients);
    }
  }

  if (data.reqsDetails.removed.length > 0) {
    if (!resolved.reqsNames) {
      resolved.reqsNames = [];
    }
    for (const reqId of data.reqsDetails.removed) {
      const req = await cache.getReq(wgId, reqId);
      resolved.reqsNames.push("- " + req?.nameClients);
    }
  }

  if (data.taxiFixedId) {
    const taxiFixed = await cache.getTaxi(wgId, centralId, data.taxiFixedId);
    resolved.taxiFixedLicense = taxiFixed.license;
  }
  return resolved;
};

export const recordCreateService = async (
  wgId: number,
  centralId: number,
  id: number,
  data: ServiceFormData,
  type: number
) => {
  const typeName = SCHEDULE_TYPES.find((t) => t.id === type)?.key ?? "now";
  try {
    const resolved = await resolveServiceData(wgId, centralId, data, {});
    const parsedData = JSON.parse(JSON.stringify(data));
    delete parsedData?.data?.audit;
    if (Object.keys(parsedData?.data).length === 0) {
      delete parsedData?.data;
    }

    audit.addToQueue(typeName, "create", id, wgId, centralId, {
      ...parsedData,
      ...resolved,
    });
  } catch (e) {
    console.error("Error recording create service audit", e);
  }
};

export const recordUpdateService = async (
  wgId: number,
  centralId: number,
  id: number,
  data: ServiceFormData,
  originalData: ServiceFormData,
  type: number
) => {
  const typeName = SCHEDULE_TYPES.find((t) => t.id === type)?.key ?? "now";
  try {
    const resolved = await resolveServiceData(wgId, centralId, data, originalData);

    let parsedData = JSON.parse(JSON.stringify(data));
    delete parsedData?.data?.audit;
    if (Object.keys(parsedData?.data || {}).length === 0) {
      delete parsedData?.data;
    }

    audit.addToQueue(typeName, "update", id, wgId, centralId, {
      id,
      ...parsedData,
      ...resolved,
    });
  } catch (e) {
    console.error("Error recording update service audit", e);
  }
};

export const recordCancelService = async (
  wgId: number,
  centralId: number,
  id: number,
  type: number
) => {
  const typeName = SCHEDULE_TYPES.find((t) => t.id === type)?.key ?? "now";
  try {
    audit.addToQueue(typeName, "cancel", id, wgId, centralId, {
      id,
    });
  } catch (e) {
    console.error("Error recording cancel service audit", e);
  }
};

export const recordFinishService = async (
  wgId: number,
  centralId: number,
  id: number,
  data: ServiceFormData,
  type: number
) => {
  const typeName = SCHEDULE_TYPES.find((t) => t.id === type)?.key ?? "now";
  try {
    let parsedData = JSON.parse(JSON.stringify(data));
    delete parsedData?.data?.audit;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (e) {
        console.warn("Text data found in finish service audit", e);
        parsedData = { raw: parsedData };
      }
    }
    audit.addToQueue(typeName, "finish", id, wgId, centralId, {
      id,
      ...parsedData,
    });
  } catch (e) {
    console.error("Error recording finish service audit", e);
  }
};

export default {
  recordCreateService,
  recordUpdateService,
  recordCancelService,
  recordFinishService,
};
